export const toDate = (v: unknown): Date | null => {
  const d = v instanceof Date ? v : typeof v === "string" ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
};

export const today = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

export const normalizeDate = (date: unknown): Date | null => {
  const d = toDate(date);
  if (!d) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

export const toMinute = (v: unknown): Date | null => {
  const d = toDate(v);
  if (!d) return null;
  const n = new Date(d);
  n.setSeconds(0, 0);
  return n;
};

export const addDays = (isoDate: string, days: number): string => {
  const date = new Date(isoDate);
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result.toISOString();
};

export const formatDateToDDMMYYYY = (isoDate: string, includeTime: boolean = false): string => {
  const [datePart, timePart] = isoDate.split("T");
  const [year, month, day] = datePart.split("-");

  if (!includeTime || !timePart) {
    return `${day}/${month}/${year}`;
  }

  const [hours, minutes] = timePart.split(":");

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const convertUtcToUae = (utcString?: string): string => {
  if (!utcString) {
    return "-";
  }

  const date = new Date(utcString);

  if (isNaN(date.getTime())) {
    throw new Error("Invalid UTC date string");
  }

  const UAE_OFFSET_MINUTES = 4 * 60;

  const utcTime = date.getTime();

  const uaeTime = new Date(utcTime + UAE_OFFSET_MINUTES * 60 * 1000);

  const pad = (num: number, size = 2) => String(num).padStart(size, "0");

  const year = uaeTime.getUTCFullYear();
  const month = pad(uaeTime.getUTCMonth() + 1);
  const day = pad(uaeTime.getUTCDate());
  const hours = pad(uaeTime.getUTCHours());
  const minutes = pad(uaeTime.getUTCMinutes());
  const seconds = pad(uaeTime.getUTCSeconds());

  const milliseconds = uaeTime.getUTCMilliseconds();
  const msPart = milliseconds ? `.${pad(milliseconds, 3)}` : "";

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${msPart}+04:00`;
};

export const getCurrentUaeDate = (): Date => {
  return new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Dubai" }));
};

export const convertUaeToUtc = (date: Date) => {
  const UAE_OFFSET_HOURS = 4;
  const utc = new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours() - UAE_OFFSET_HOURS,
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds(),
    ),
  );

  return utc.toISOString();
};

export const isAfterToday = (date: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const value = new Date(date);
  value.setHours(0, 0, 0, 0);

  return value > getCurrentUaeDate();
};

export const isBeforeToday = (date: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const value = new Date(date);
  value.setHours(0, 0, 0, 0);

  return value < today;
};

interface CalculateShipAgeParams {
  deliveryDate: string | undefined;
  t: (key: string) => string;
}

export const calculateShipAge = (
  deliveryDate: CalculateShipAgeParams["deliveryDate"],
  t: CalculateShipAgeParams["t"],
): number | string => {
  if (!deliveryDate) return 0;
  const date = new Date(deliveryDate);
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const hasHadAniversaryThisYear =
    today.getMonth() > date.getMonth() ||
    (today.getMonth() === date.getMonth() && today.getDate() >= date.getDate());
  if (!hasHadAniversaryThisYear) {
    age -= 1;
  }
  return age >= 0 ? age : t("common.invalid-year");
};
